import { useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, CandidateAvatar, StatusBadge } from "@/components/Common";
import { ContractButton, FeeStatus } from "@/components/Signature";
import { DateTimePicker } from "@/components/DatePicker";
import { api, formatErr, fmtDate, fmtDay, useFetch } from "@/lib/api";

const HIRE_STATUSES = ["Pending", "In Review", "Approved", "Placed", "Declined"];
const INT_STATUSES = ["Requested", "Scheduled", "Completed", "Cancelled"];

const Card = ({ item, children, statusEl }) => (
  <div data-testid={`admin-request-${item.id}`} className="grid gap-4 rounded-md border border-stone-200 bg-white p-5 lg:grid-cols-[1fr_1fr_auto] lg:items-center">
    <div className="flex items-center gap-3">
      <CandidateAvatar candidate={item.candidate} className="h-11 w-11" />
      <div><p className="font-medium text-stone-900">{item.candidate.name}</p><p className="text-xs text-stone-500">{item.candidate.title}</p></div>
    </div>
    <div className="text-sm">
      <p className="font-medium text-stone-900">{item.business.company || item.business.name}</p>
      <p className="text-xs text-stone-500">{item.business.name} · {item.business.email}</p>
      <div className="mt-1 text-xs text-stone-600">{children}</div>
    </div>
    <div className="flex flex-col gap-2 lg:items-end">{statusEl}<span className="text-[11px] font-mono text-stone-400">{fmtDate(item.created_at)}</span></div>
  </div>
);

const StatusSelect = ({ value, options, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="w-40"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const ScheduleDialog = ({ item, onClose, onSave }) => {
  const [f, setF] = useState({ scheduled_at: "", meeting_link: "", admin_note: "" });
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="schedule-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Confirm interview</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">Proposed: {item?.preferred_times.map(fmtDate).join(" · ")} ({item?.timezone})</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Confirmed time</Label><DateTimePicker testId="schedule-time-input" value={f.scheduled_at} onChange={(v) => setF({ ...f, scheduled_at: v })} /></div>
          <div className="space-y-1.5"><Label>Meeting link</Label><Input data-testid="schedule-link-input" placeholder="https://meet.google.com/…" value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Note to client</Label><Textarea data-testid="schedule-note-input" rows={2} value={f.admin_note} onChange={(e) => setF({ ...f, admin_note: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="schedule-save-button" disabled={!f.scheduled_at} onClick={() => onSave(item, { status: "Scheduled", ...f })}>Confirm & notify</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminRequests() {
  const hires = useFetch("/admin/hire-requests");
  const ints = useFetch("/admin/interviews");
  const offers = useFetch("/offers");
  const [scheduling, setScheduling] = useState(null);

  const patch = async (kind, store, item, body) => {
    try {
      const { data } = await api.patch(`/admin/${kind}/${item.id}`, body);
      store.setData((prev) => prev.map((x) => (x.id === item.id ? data : x)));
      toast.success("Updated");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Pipeline" title="Hires & interviews" desc="Review client requests and update their status." />
      <Tabs defaultValue="interviews">
        <TabsList className="mb-6">
          <TabsTrigger value="interviews" data-testid="admin-tab-interviews">Interviews ({ints.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="hires" data-testid="admin-tab-hires">Hire requests ({hires.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="offers" data-testid="admin-tab-offers">Offers ({offers.data?.length ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="offers" className="space-y-3">
          {offers.data?.length === 0 && <p className="text-sm text-stone-500">No offers yet.</p>}
          {(offers.data || []).map((o) => (
            <Card key={o.id} item={o} statusEl={<StatusBadge status={o.status} />}>
              {o.position} · ${o.hourly_rate}/hr · {o.hours_per_week} hrs/wk{o.licensing_fee > 0 && ` · licensing fee $${o.licensing_fee}`}
              {o.response_note && <p className="mt-1 italic">"{o.response_note}"</p>}
              <ContractButton offer={o} className="mt-1 block" />
              <FeeStatus offer={o} />
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="interviews" className="space-y-3">
          {ints.data?.length === 0 && <p className="text-sm text-stone-500">No interview requests yet.</p>}
          {(ints.data || []).map((i) => (
            <Card key={i.id} item={i} statusEl={
              <div className="flex flex-wrap items-center gap-2">
                <StatusSelect value={i.status} options={INT_STATUSES} testId={`interview-status-${i.id}`} onChange={(v) => (v === "Scheduled" ? setScheduling(i) : patch("interviews", ints, i, { status: v }))} />
                <Button size="sm" variant="outline" data-testid={`schedule-button-${i.id}`} onClick={() => setScheduling(i)}>Schedule</Button>
              </div>
            }>
              {i.scheduled_at ? <span className="text-blue-700">Confirmed {fmtDate(i.scheduled_at)}</span> : <>Proposed: {i.preferred_times.map(fmtDate).join(" · ")}</>}
              {i.notes && <p className="mt-1 italic">"{i.notes}"</p>}
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="hires" className="space-y-3">
          {hires.data?.length === 0 && <p className="text-sm text-stone-500">No hire requests yet.</p>}
          {(hires.data || []).map((h) => (
            <Card key={h.id} item={h} statusEl={<div className="flex items-center gap-2"><StatusBadge status={h.status} /><StatusSelect value={h.status} options={HIRE_STATUSES} testId={`hire-status-${h.id}`} onChange={(v) => patch("hire-requests", hires, h, { status: v })} /></div>}>
              {h.engagement} · {h.hours_per_week} hrs/wk{h.start_date && ` · Start ${fmtDay(h.start_date)}`}
              {h.notes && <p className="mt-1 italic">"{h.notes}"</p>}
            </Card>
          ))}
        </TabsContent>
      </Tabs>
      <ScheduleDialog key={scheduling?.id} item={scheduling} onClose={() => setScheduling(null)} onSave={async (i, body) => { await patch("interviews", ints, i, body); setScheduling(null); }} />
    </div>
  );
}
